import React from 'react';

interface CatharsisTypeDist {
  type: string;
  count: number;
  percentage: number;
}

interface CatharsisScene {
  scene_id: string;
  scene_title: string;
  adjusted_score: number;
  best_type: string;
  best_type_desc: string;
  matched_types: string[];
}

interface SpeechActScene {
  scene_id: string;
  scene_title: string;
  score: number;
  acts_count: number;
  unique_categories: number;
  verdict: string;
}

interface GapArc {
  character: string;
  type: string;
  description: string;
  scenes: number;
}

interface StaleGap {
  character: string;
  ignorant_ratio: number;
  recommendation: string;
}

interface CriticNarrativeAnalysis {
  catharsis_analysis?: {
    summary?: {
      mean_score?: number;
      max_score?: number;
      best_scene?: string;
      best_scene_type?: string;
      weak_scene_count?: number;
    };
    type_distribution?: CatharsisTypeDist[];
    per_scene?: CatharsisScene[];
    recommendation?: string;
  };
  speech_act_analysis?: {
    mean_score?: number;
    verdict?: string;
    per_scene?: SpeechActScene[];
    recommendation?: string;
  };
  information_gap_analysis?: {
    dramatic_irony_score?: number;
    suspense_score?: number;
    information_gap_score?: number;
    gap_arcs?: GapArc[];
    stale_gaps?: StaleGap[];
    recommendation?: string;
  };
}

interface CriticNarrativePanelProps {
  analysis?: CriticNarrativeAnalysis | null;
  locale?: 'zh' | 'en';
}

const LABELS = {
  zh: {
    title: '叙事深度分析',
    catharsis: '爽点质量分析',
    typeDistribution: '爽点类型分布',
    sceneScores: '场景爽点评分',
    speechAct: '言语行为分析',
    infoGap: '信息差图谱',
    dramaticIrony: '戏剧反讽',
    suspense: '悬念张力',
    infoGapScore: '信息差总分',
    gapArcs: '信息差弧线',
    staleGaps: '陈旧信息差',
    meanScore: '平均分',
    verdict: '判定',
    strong: '强',
    moderate: '中',
    weak: '弱',
    recommendation: '建议',
    noData: '暂无叙事深度分析数据。运行连贯性校验后将自动更新。',
    scene: '场景',
    type: '类型',
    score: '得分',
    acts: '言语行为数',
    categories: '类别数',
  },
  en: {
    title: 'Narrative Depth Analysis',
    catharsis: 'Catharsis Quality',
    typeDistribution: 'Catharsis Type Distribution',
    sceneScores: 'Scene Catharsis Scores',
    speechAct: 'Speech-Act Analysis',
    infoGap: 'Information Gap Map',
    dramaticIrony: 'Dramatic Irony',
    suspense: 'Suspense',
    infoGapScore: 'Info-Gap Score',
    gapArcs: 'Gap Arcs',
    staleGaps: 'Stale Gaps',
    meanScore: 'Mean Score',
    verdict: 'Verdict',
    strong: 'Strong',
    moderate: 'Moderate',
    weak: 'Weak',
    recommendation: 'Recommendation',
    noData: 'No narrative depth analysis yet. Run continuity validation to update.',
    scene: 'Scene',
    type: 'Type',
    score: 'Score',
    acts: 'Speech Acts',
    categories: 'Categories',
  },
};

function getBarColor(score: number): string {
  if (score >= 7.5) return '#10b981'; // green
  if (score >= 5.0) return '#f59e0b'; // amber
  return '#ef4444'; // red
}

function getVerdictClass(verdict: string): string {
  if (verdict === 'strong') return 'is-online';
  if (verdict === 'moderate') return 'is-idle';
  return 'is-warning';
}

const CriticNarrativePanel: React.FC<CriticNarrativePanelProps> = ({ analysis, locale = 'zh' }) => {
  const t = LABELS[locale];

  if (!analysis) {
    return (
      <div className="panel-card">
        <div className="panel-card__header">
          <div className="panel-title">
            <span className="panel-title__icon">📊</span>
            <h2>{t.title}</h2>
          </div>
        </div>
        <p className="empty-copy">{t.noData}</p>
      </div>
    );
  }

  const catharsis = analysis.catharsis_analysis;
  const speechAct = analysis.speech_act_analysis;
  const infoGap = analysis.information_gap_analysis;

  const maxTypeCount = Math.max(
    ...(catharsis?.type_distribution?.map((d) => d.count) || [1]),
    1
  );

  return (
    <div className="panel-card">
      <div className="panel-card__header">
        <div className="panel-title">
          <span className="panel-title__icon">📊</span>
          <h2>{t.title}</h2>
        </div>
      </div>

      {/* Catharsis Analysis */}
      {catharsis && (
        <details className="panel-dropdown panel-dropdown--content" open>
          <summary className="panel-dropdown__summary">{t.catharsis}</summary>
          <div className="panel-dropdown__body">
            <div className="impact-metrics">
              <span className="app-pill">
                <span>🔥</span>
                <span>
                  {t.meanScore}: {catharsis.summary?.mean_score ?? '--'}
                </span>
              </span>
              <span className="app-pill">
                <span>⭐</span>
                <span>
                  {t.score}: {catharsis.summary?.max_score ?? '--'}
                </span>
              </span>
            </div>

            {/* Type Distribution Bar Chart */}
            {catharsis.type_distribution && catharsis.type_distribution.length > 0 && (
              <div style={{ margin: '12px 0' }}>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>{t.typeDistribution}</div>
                {catharsis.type_distribution.map((item) => (
                  <div key={item.type} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ width: '120px', fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.type}
                    </span>
                    <div style={{ flex: 1, height: '18px', background: '#f0f0f0', borderRadius: '4px', overflow: 'hidden', margin: '0 8px' }}>
                      <div
                        style={{
                          width: `${(item.count / maxTypeCount) * 100}%`,
                          height: '100%',
                          background: getBarColor(item.count * 2 + 5),
                          borderRadius: '4px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    <span style={{ fontSize: '12px', color: '#666', minWidth: '40px', textAlign: 'right' }}>
                      {item.count} ({Math.round((item.percentage || 0) * 100)}%)
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Per-Scene Scores */}
            {catharsis.per_scene && catharsis.per_scene.length > 0 && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>{t.sceneScores}</div>
                {catharsis.per_scene.map((s) => (
                  <div key={s.scene_id} className="impact-row">
                    <div className="impact-row__title">
                      <span
                        className="impact-type"
                        style={{ background: getBarColor(s.adjusted_score), color: '#fff' }}
                      >
                        {s.adjusted_score?.toFixed(1) ?? '--'}
                      </span>
                      <strong>{s.scene_title || s.scene_id}</strong>
                    </div>
                    <div className="impact-note">{s.best_type_desc || s.best_type || '-'}</div>
                  </div>
                ))}
              </div>
            )}

            {catharsis.recommendation && (
              <p className="impact-note impact-note--muted" style={{ marginTop: '8px' }}>
                💡 {catharsis.recommendation}
              </p>
            )}
          </div>
        </details>
      )}

      {/* Speech-Act Analysis */}
      {speechAct && (
        <details className="panel-dropdown panel-dropdown--content" open>
          <summary className="panel-dropdown__summary">{t.speechAct}</summary>
          <div className="panel-dropdown__body">
            <div className="impact-metrics">
              <span className={`app-pill ${getVerdictClass(speechAct.verdict || '')}`}>
                <span>🗣️</span>
                <span>
                  {t.verdict}: {speechAct.verdict || '--'}
                </span>
              </span>
              <span className="app-pill">
                <span>📈</span>
                <span>
                  {t.meanScore}: {speechAct.mean_score ?? '--'}
                </span>
              </span>
            </div>
            {speechAct.per_scene && speechAct.per_scene.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {speechAct.per_scene.map((s) => (
                  <div key={s.scene_id} className="impact-row">
                    <div className="impact-row__title">
                      <span
                        className="impact-type"
                        style={{ background: getBarColor(s.score), color: '#fff' }}
                      >
                        {s.score?.toFixed(1) ?? '--'}
                      </span>
                      <strong>{s.scene_title || s.scene_id}</strong>
                    </div>
                    <div className="impact-note">
                      {t.acts}: {s.acts_count} | {t.categories}: {s.unique_categories}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {speechAct.recommendation && (
              <p className="impact-note impact-note--muted" style={{ marginTop: '8px' }}>
                💡 {speechAct.recommendation}
              </p>
            )}
          </div>
        </details>
      )}

      {/* Information-Gap Analysis */}
      {infoGap && (
        <details className="panel-dropdown panel-dropdown--content" open>
          <summary className="panel-dropdown__summary">{t.infoGap}</summary>
          <div className="panel-dropdown__body">
            <div className="impact-metrics">
              <span className="app-pill">
                <span>🎭</span>
                <span>
                  {t.dramaticIrony}: {infoGap.dramatic_irony_score ?? '--'}
                </span>
              </span>
              <span className="app-pill">
                <span>😰</span>
                <span>
                  {t.suspense}: {infoGap.suspense_score ?? '--'}
                </span>
              </span>
              <span className="app-pill">
                <span>🔍</span>
                <span>
                  {t.infoGapScore}: {infoGap.information_gap_score ?? '--'}
                </span>
              </span>
            </div>

            {infoGap.gap_arcs && infoGap.gap_arcs.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{t.gapArcs}</div>
                {infoGap.gap_arcs.map((arc, idx) => (
                  <div key={idx} className="impact-row">
                    <div className="impact-row__title">
                      <span className="impact-type is-idle">{arc.type}</span>
                      <strong>{arc.character}</strong>
                    </div>
                    <div className="impact-note">{arc.description}</div>
                  </div>
                ))}
              </div>
            )}

            {infoGap.stale_gaps && infoGap.stale_gaps.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{t.staleGaps}</div>
                {infoGap.stale_gaps.map((gap, idx) => (
                  <div key={idx} className="impact-row">
                    <div className="impact-row__title">
                      <span className="impact-type is-warning">STALE</span>
                      <strong>{gap.character}</strong>
                    </div>
                    <div className="impact-note">{gap.recommendation}</div>
                  </div>
                ))}
              </div>
            )}

            {infoGap.recommendation && (
              <p className="impact-note impact-note--muted" style={{ marginTop: '8px' }}>
                💡 {infoGap.recommendation}
              </p>
            )}
          </div>
        </details>
      )}
    </div>
  );
};

export default CriticNarrativePanel;
